import React, { createContext, useCallback, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';

type RefreshFn = () => Promise<void>;

interface RefreshContextValue {
  registerRefresh: (path: string, fn: RefreshFn) => void;
  unregisterRefresh: (path: string) => void;
  triggerRefresh: () => Promise<void>;
}

const RefreshContext = createContext<RefreshContextValue | null>(null);

export const RefreshProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const handlersRef = useRef<Map<string, RefreshFn>>(new Map());
  const location = useLocation();

  const registerRefresh = useCallback((path: string, fn: RefreshFn) => {
    handlersRef.current.set(path, fn);
  }, []);

  const unregisterRefresh = useCallback((path: string) => {
    handlersRef.current.delete(path);
  }, []);

  const triggerRefresh = useCallback(async (): Promise<void> => {
    const path = location.pathname;
    const handler = handlersRef.current.get(path);
    if (handler) {
      await handler();
    }
  }, [location.pathname]);

  const value: RefreshContextValue = {
    registerRefresh,
    unregisterRefresh,
    triggerRefresh
  };

  return (
    <RefreshContext.Provider value={value}>
      {children}
    </RefreshContext.Provider>
  );
};

export const useRefresh = (refreshFn: RefreshFn): void => {
  const context = useContext(RefreshContext);
  const location = useLocation();
  const path = location.pathname;
  const fnRef = React.useRef(refreshFn);
  fnRef.current = refreshFn;

  React.useEffect(() => {
    if (!context) return;
    const fn = () => fnRef.current();
    context.registerRefresh(path, fn);
    return () => context.unregisterRefresh(path);
  }, [context, path]);
};

export const useTriggerRefresh = (): (() => Promise<void>) => {
  const context = useContext(RefreshContext);
  if (!context) {
    return async () => {};
  }
  return context.triggerRefresh;
};
